/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect, useState } from "react";
import { useQuery } from "@apollo/client";

// molecules
import { BaseCard, BaseCardAction, BaseCardHeader, PageHeader } from "@molecules/index";

// Hooks
import { useSelectClient } from "@atoms/SelectClient";

// store
import Store from "@store/jotai";

// Telluria
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// atoms
import { Loading } from "@atoms/index";
import ShowMoreButton from "@atoms/ShowMoreButton";
import AlertContentByClient from "@atoms/AlertContentByClient";

// Libraries
import { Grid } from "@libraries/mui";

// graphql
import {
  ConcretingRequestQueriesGetAllWithSortedPaginationArgs,
  ConcretingRequestType,
  ECommandResultStatus,
  EConcretingRequestStatus,
  ESort,
  EWhereClausesOperators
} from "@store/graphql/helpers/types.graphql";

// Languages
import useTranslation from "@languages/useTranslation";
import { AlertMessages, CustomerMessages } from "@languages/interfaces";

import {
  concretingRequestAllWithSortedPagination,
  TConcretingRequestAllWithSortedPaginationQueries
} from "@graphql/groups/concretingRequest/query.graphql";
import * as Styled from "./styles";
import Office from "../Office";

const ConcretingApprovals: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();

  const limitPerPage = 12;

  const [nextPage, setNextPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  // state to get status from modal to approve concretings
  const [approvalModalIsOpen, setApprovalModalIsOpen] = useState(false);

  const { value: selectedClient } = useSelectClient();

  const {
    value: concretingRequests,
    setValue: setConcretingRequests,
    filteredConcretingRequests,
    setFilteredConcretingRequests
  } = Store.useConcretingRequests();

  const {
    loading: loadingConcretingRequests,
    error: errorConcretingRequests,
    data: dataConcretingRequests,
    refetch: refetchConcretingRequests
  } = useQuery<
    TConcretingRequestAllWithSortedPaginationQueries,
    ConcretingRequestQueriesGetAllWithSortedPaginationArgs
  >(
    concretingRequestAllWithSortedPagination([
      "id",
      "status",
      "requestedDate",
      "requestedQuantity",
      "quantityPerConcreteMixer",
      "toConfirm",
      "travelsReleased",
      "interval",
      "pieceToBeConcreted",
      "pump",
      "observationPump",
      "pipingDistance",
      "observationConcreting",
      "requester",
      "requesterPhoneNumber",
      "contract.code",
      "concreteBatchingPlant.name",
      "contractConcreteRecipe.concreteRecipe.description",
      "contract.construction.name",
      "contract.construction.address",
      "contract.construction.county",
      "contract.construction.number",
      "contract.construction.complement",
      "contract.contractSellers.seller.name",
      "messages.id",
      "messages.message",
      "messages.createdAt",
      "messages.user.id",
      "messages.user.fullName",
      "messages.user.role"
    ]),
    {
      fetchPolicy: "network-only",
      variables: {
        page: nextPage,
        perPage: limitPerPage,
        sort: [{
          field: "requestedDate",
          sortDirection: ESort.DESC
        }],
        where: [
          {
            property: "status",
            operator: EWhereClausesOperators.IN,
            value: `${EConcretingRequestStatus.PENDING}|${EConcretingRequestStatus.REQUESTED}`
          }
        ]
      }
    }
  );

  const loadConcretingRequests = useCallback(async () => {
    const response = dataConcretingRequests?.concretingRequest.getAllWithSortedPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      setTotalPages(response.pageCount);
      const concretingRequestsList = response.result;

      if (nextPage > 1) {
        setConcretingRequests(concretingRequests.concat(concretingRequestsList as ConcretingRequestType[]));
      } else {
        setConcretingRequests(
          concretingRequestsList as ConcretingRequestType[]
        );
      }
    }
  }, [dataConcretingRequests, setConcretingRequests]);

  useEffect(() => {
    if (dataConcretingRequests) loadConcretingRequests();
    if (errorConcretingRequests) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorConcretingRequests.message
      });
    }
  }, [
    addToast,
    dataConcretingRequests,
    errorConcretingRequests,
    loadConcretingRequests,
    nextPage
  ]);

  useEffect(() => {
    setConcretingRequests([]);
    setNextPage(1);
  }, [selectedClient, setConcretingRequests]);

  useEffect(() => {
    setFilteredConcretingRequests(concretingRequests);
  }, [concretingRequests, setFilteredConcretingRequests]);

  useEffect(() => () => {
    setConcretingRequests([]);
    setFilteredConcretingRequests([]);
  }, [setConcretingRequests, setFilteredConcretingRequests]);

  useEffect(() => {
    if (approvalModalIsOpen && dataConcretingRequests) return;

    refetchConcretingRequests();
  }, [approvalModalIsOpen, dataConcretingRequests, refetchConcretingRequests]);

  const handleNextPage = () => setNextPage(nextPage + 1);

  return (
    <Office showSelectClient={false}>
      <PageHeader
        url="/"
        title={t(CustomerMessages.applicationConcretingApproval)}
      />

      <Styled.Container>
        <Grid container spacing={2}>
          {loadingConcretingRequests && (
            <Grid item xs={12}>
              <Loading loading />
            </Grid>
          )}
          {!loadingConcretingRequests
            && filteredConcretingRequests?.length === 0
            && (
              <Grid item xs={12}>
                <AlertContentByClient alertMessage={t(AlertMessages.noConcretingApproval)} iconColor="yellow" />
              </Grid>
            )}
          {!loadingConcretingRequests
            && filteredConcretingRequests
            && filteredConcretingRequests.map((concretingRequest) => (
              <Grid
                key={`grid-${concretingRequest.id}`}
                item
                xs={12}
                sm={6}
                md={4}
              >
                <BaseCard
                  key={concretingRequest.id}
                  id={concretingRequest.id}
                  header={
                    <BaseCardHeader
                      type="concretingRequest"
                      data={concretingRequest}
                    />
                    }
                  actions={
                    <BaseCardAction
                      type="concretingApproval"
                      concretingRequest={concretingRequest}
                      setApprovalModalIsOpen={setApprovalModalIsOpen}
                    />
                    }
                />
              </Grid>
            ))}
        </Grid>
        {filteredConcretingRequests
          && nextPage < totalPages
          && (
          <Styled.ContainerShowMoreButton>
            <ShowMoreButton onClick={handleNextPage} />
          </Styled.ContainerShowMoreButton>
          )}
      </Styled.Container>
    </Office>
  );
};

export default ConcretingApprovals;
